'use client';

import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';

type Props = {
  children: ReactNode;
  className?: string;
  delay?: number;
  as?: keyof React.JSX.IntrinsicElements;
  style?: CSSProperties;
};

export default function Reveal({
  children,
  className = '',
  delay = 0,
  as = 'div',
  style
}: Props) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (!('IntersectionObserver' in window)) {
      node.classList.add('in');
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            node.classList.add('in');
            io.unobserve(node);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -80px 0px' }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  const Tag = as as 'div';
  const combined: CSSProperties = { ...(style ?? {}) };
  if (delay) combined['--delay' as keyof CSSProperties] = `${delay}ms` as never;

  return (
    <Tag
      ref={ref as never}
      className={`reveal ${className}`.trim()}
      style={combined}
    >
      {children}
    </Tag>
  );
}
